import {
  Box,
  Button,
  Card,
  DialogTitle,
  Flex,
  HStack,
  Heading,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { FaPlus } from "react-icons/fa"
import { FiUserCheck } from "react-icons/fi"

import type { MemberAdd, UserPublic } from "@/client"
import { ImpersonationService, OrganizationsService } from "@/client"
import {
  type OrgBulkMemberResult,
  uploadOrgMembersCsv,
} from "@/client/bulkUpload"
import type { ApiError } from "@/client/core/ApiError"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { SkeletonText } from "@/components/ui/skeleton"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

const PER_PAGE = 20

interface OrganizationMembersProps {
  orgId: string
  orgName: string
}

function AddMemberDialog({ orgId }: { orgId: string }) {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<MemberAdd>({
    mode: "onBlur",
    defaultValues: {
      email: "",
      role: "student",
    },
  })

  const mutation = useMutation({
    mutationFn: (data: MemberAdd) =>
      OrganizationsService.addMemberToOrganization({
        orgId,
        requestBody: data,
      }),
    onSuccess: () => {
      showSuccessToast("Member added successfully")
      reset()
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["members", orgId] })
    },
  })

  const onSubmit = (data: MemberAdd) => {
    mutation.mutate(data)
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button my={4}>
          <FaPlus fontSize="16px" />
          Add Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.email}
                errorText={errors.email?.message}
                label="User Email"
                helperText="Enter the user's email address"
              >
                <Input
                  id="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  placeholder="user@example.com"
                  type="email"
                />
              </Field>

              <Field
                required
                invalid={!!errors.role}
                errorText={errors.role?.message}
                label="Role"
              >
                <NativeSelectRoot>
                  <NativeSelectField
                    id="role"
                    {...register("role", { required: "Role is required" })}
                  >
                    <option value="">Select role</option>
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="student">Student</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              type="submit"
              disabled={!isValid}
              loading={isSubmitting}
            >
              Add
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

function BulkUploadDialog({ orgId }: { orgId: string }) {
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<OrgBulkMemberResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const resetState = () => {
    setFile(null)
    setPreview(null)
  }

  const handlePreview = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      const result = await uploadOrgMembersCsv({
        orgId,
        file,
        previewOnly: true,
      })
      setPreview(result)
    } catch (err) {
      handleError(err as ApiError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      const result = await uploadOrgMembersCsv({
        orgId,
        file,
        previewOnly: false,
      })
      setPreview(result)
      showSuccessToast("Bulk upload completed")
      queryClient.invalidateQueries({ queryKey: ["members", orgId] })
    } catch (err) {
      handleError(err as ApiError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownloadTemplate = () => {
    const csv = [
      "email,full_name,role",
      "teacher1@example.com,Teacher One,teacher",
      "admin1@example.com,Admin One,admin",
    ].join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "org-members-template.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <DialogRoot
      placement="center"
      size={{ base: "xs", md: "lg" }}
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (!open) {
          resetState()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">Bulk upload CSV</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk upload members</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text color="fg.muted">
              Upload a CSV with columns: <strong>email</strong> (required),
              <strong>full_name</strong> (optional), and <strong>role</strong>
              (admin, teacher, or student — defaults to student).
            </Text>
            <Button
              alignSelf="flex-start"
              size="sm"
              variant="subtle"
              onClick={handleDownloadTemplate}
            >
              Download sample CSV
            </Button>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const selected = e.target.files?.[0] ?? null
                setFile(selected)
                setPreview(null)
              }}
            />

            {preview && (
              <VStack align="stretch" gap={2} mt={2}>
                <Text fontWeight="medium">Preview summary</Text>
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="fg.muted">
                    Valid rows: {preview.valid_rows} / {preview.total_rows}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Created users: {preview.created_users}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Added memberships: {preview.added_memberships}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    Skipped existing: {preview.skipped_existing_memberships}
                  </Text>
                </HStack>

                {preview.errors?.length ? (
                  <VStack align="stretch" gap={1} maxH="180px" overflowY="auto">
                    <Text fontSize="sm" color="red.600">
                      Errors ({preview.errors.length})
                    </Text>
                    {preview.errors.map((err) => (
                      <Text
                        key={`${err.row}-${err.email ?? err.row}`}
                        fontSize="sm"
                        color="red.700"
                      >
                        Row {err.row}: {err.error}
                        {err.email ? ` (${err.email})` : ""}
                      </Text>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="green.600">
                    No validation errors detected.
                  </Text>
                )}
              </VStack>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter gap={2} flexWrap="wrap">
          <DialogActionTrigger asChild>
            <Button
              variant="subtle"
              colorPalette="gray"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!file || isSubmitting}
            loading={isSubmitting}
          >
            Preview
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleUpload}
            disabled={!file || isSubmitting}
            loading={isSubmitting}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

function OrganizationMembers({ orgId, orgName }: OrganizationMembersProps) {
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1) // Reset to first page on new search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data, isLoading } = useQuery({
    queryKey: ["members", orgId, page, debouncedSearch],
    queryFn: () =>
      OrganizationsService.listOrganizationMembers({
        orgId,
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search: debouncedSearch || undefined,
      }),
  })

  const impersonateMutation = useMutation({
    mutationFn: (userId: string) =>
      ImpersonationService.startImpersonation({
        requestBody: { user_id: userId },
      }),
    onSuccess: (data) => {
      localStorage.setItem("access_token", data.access_token)
      showSuccessToast("Now impersonating user")
      // Reload immediately to refresh all state with new token
      // Do NOT invalidate queries before reload to avoid race conditions
      window.location.reload()
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      OrganizationsService.removeMemberFromOrganization({
        orgId,
        userId,
      }),
    onSuccess: () => {
      showSuccessToast("Member removed successfully")
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["members", orgId] })
    },
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      OrganizationsService.updateMemberRole({
        orgId,
        userId,
        requestBody: { role },
      }),
    onSuccess: () => {
      showSuccessToast("Role updated successfully")
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["members", orgId] })
    },
  })

  const [memberToRemove, setMemberToRemove] = useState<string | null>(null)

  // Check if current user can impersonate a member
  const canImpersonate = (memberUserId: string) => {
    if (!currentUser) return false
    // Can't impersonate yourself
    if (currentUser.id === memberUserId) return false
    // Superusers can impersonate anyone (backend will prevent superuser impersonation)
    if (currentUser.is_superuser) return true
    // Regular admins can impersonate within their org (backend handles verification)
    return true
  }

  return (
    <VStack align="stretch" gap={6} width="full">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        gap={4}
      >
        <Heading size="md">Members · {orgName}</Heading>
        <HStack gap={3} flexWrap="wrap">
          <BulkUploadDialog orgId={orgId} />
          <AddMemberDialog orgId={orgId} />
        </HStack>
      </Flex>

      {/* Search Input */}
      <Field label="Search members">
        <Input
          placeholder="Search by email or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          mb={4}
        />
      </Field>

      {isLoading ? (
        <VStack gap={2}>
          <SkeletonText />
          <SkeletonText />
        </VStack>
      ) : (
        <>
          <Text fontSize="sm" color="fg.muted" mb={2}>
            Showing {data?.data.length || 0} of {data?.count || 0} members
          </Text>

          {/* Desktop */}
          <Box display={{ base: "none", md: "block" }}>
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Email</Table.ColumnHeader>
                  <Table.ColumnHeader>Role</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {!data?.data || data.data.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Text textAlign="center" color="fg.muted">
                        No members yet
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  data.data.map((member) => (
                    <Table.Row key={member.id}>
                      <Table.Cell>{member.email}</Table.Cell>
                      <Table.Cell>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={member.role}
                            onChange={(e) =>
                              updateRoleMutation.mutate({
                                userId: member.user_id,
                                role: e.target.value,
                              })
                            }
                          >
                            <option value="admin">Admin</option>
                            <option value="teacher">Teacher</option>
                            <option value="student">Student</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Table.Cell>
                      <Table.Cell>
                        {canImpersonate(member.user_id) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette="orange"
                            onClick={() =>
                              impersonateMutation.mutate(member.user_id)
                            }
                            loading={impersonateMutation.isPending}
                            mr={2}
                          >
                            <FiUserCheck />
                            Impersonate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => setMemberToRemove(member.user_id)}
                        >
                          Remove
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile cards */}
          <VStack
            display={{ base: "flex", md: "none" }}
            gap={3}
            align="stretch"
          >
            {!data?.data || data.data.length === 0 ? (
              <Text textAlign="center" color="fg.muted" py={4}>
                No members yet
              </Text>
            ) : (
              data.data.map((member) => (
                <Card.Root key={member.id}>
                  <Card.Body>
                    <Text fontWeight="medium">{member.email}</Text>
                    <Box mt={2}>
                      <Text fontSize="xs" color="fg.muted" mb={1}>
                        Role
                      </Text>
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={member.role}
                          onChange={(e) =>
                            updateRoleMutation.mutate({
                              userId: member.user_id,
                              role: e.target.value,
                            })
                          }
                        >
                          <option value="admin">Admin</option>
                          <option value="teacher">Teacher</option>
                          <option value="student">Student</option>
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Box>
                    <Flex gap={2} mt={3} wrap="wrap">
                      {canImpersonate(member.user_id) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="orange"
                          onClick={() =>
                            impersonateMutation.mutate(member.user_id)
                          }
                          loading={impersonateMutation.isPending}
                        >
                          <FiUserCheck />
                          Impersonate
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => setMemberToRemove(member.user_id)}
                      >
                        Remove
                      </Button>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              ))
            )}
          </VStack>

          {/* Pagination */}
          {(data?.count || 0) > PER_PAGE && (
            <Flex justifyContent="center" mt={4}>
              <PaginationRoot
                count={data?.count || 0}
                pageSize={PER_PAGE}
                page={page}
                onPageChange={(e) => setPage(e.page)}
              >
                <Flex gap={2}>
                  <PaginationPrevTrigger />
                  <PaginationItems />
                  <PaginationNextTrigger />
                </Flex>
              </PaginationRoot>
            </Flex>
          )}
        </>
      )}

      {/* Remove confirmation dialog */}
      <DialogRoot
        open={!!memberToRemove}
        onOpenChange={({ open }) => !open && setMemberToRemove(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text>Are you sure you want to remove this member?</Text>
          </DialogBody>
          <DialogFooter gap={2}>
            <Button variant="subtle" onClick={() => setMemberToRemove(null)}>
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={() => {
                if (memberToRemove) {
                  removeMemberMutation.mutate(memberToRemove)
                  setMemberToRemove(null)
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </VStack>
  )
}

export default OrganizationMembers
